// services/withdrawalService.ts
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// -------------------- Types --------------------

export interface WithdrawalEligibilityResponse {
  has_withdrawal_access: boolean;
  allowed_channels: string[];
  allowed_actions: string[];
  allowed_operators: string[];
}

// (Optionnel) shape "UI" si ton app l’attend encore
export interface WithdrawalEligibility {
  has_withdrawal_access: boolean;
  merchant_code: string;
  username: string;
  cached?: boolean;
  timestamp?: string;
  error?: string;
  allowed_channels?: string[];
  allowed_actions?: string[];
  allowed_operators?: string[];
}

export interface BalanceSummary {
  merchant_code: string;
  currency: string;
  total_balance: number;
  operator_details: Array<{
    method: string;
    realtime_withdrawal: number;
    realtime_deposit: number;
    reserved_withdrawal: number;
    available_for_withdrawal: number;
  }>;
  available_for_withdrawal: number;
}

/**
 * ✅ Payload réel du backend: POST /cashout/mobilemoney
 * correspond à CustomerMobileMoneyCashoutRequest
 */
export interface CustomerMobileMoneyCashoutRequest {
  amount: number;
  currency: string;          // CDF | USD ...
  method: string;            // mpesa | orange | airtel | afrimoney
  customer_number: string;
  firstname: string;
  lastname: string;
  "e-mail": string;          // backend accepte "e-mail"
  reference?: string;
  note?: string;
}

/**
 * Réponse backend: TransferResponse
 */
export interface TransferResponse {
  merchant_code: string;
  transfer_id: string;
  reference: string;
  currency: string;
  amount: number;
  from_wallet: any;
  to_wallet: any;
  created_at: string;
  status: string;     // PENDING / SUCCESS / FAILED ...
  message?: string;
}

export interface WithdrawalHistoryItem {
  id: string;
  merchant_code: string;
  method: string;
  currency: string;
  amount: number;
  action: string;
  status: string;
  customer_number: string;
  reference: string;
  note?: string;
  created_at: string;
  updated_at: string;
}

export interface WithdrawalHistoryResponse {
  transactions: WithdrawalHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export type UIWithdrawalStatus = "successful" | "failed" | "processing" | "pending";

export interface WithdrawalInitiateRequest {
  amount: number;
  currency: string;
  method: string;
  phone?: string;
  otp_code?: string;
  reference?: string;
  otp_reference?: string;
}

export interface WithdrawalInitiateResponse {
  status: string;  // otp_required | success | pending | pending_approval
  message: string;
  reference: string;
  requires_otp?: boolean;
  note?: string;
  switch_reference?: string;
  amount?: number;
  currency?: string;
  method?: string;
}

// -------------------- Config --------------------

const API_TIMEOUTS = {
  eligibility: 5000,
  balanceSummary: 8000,
  history: 10000,
  initiate: 15000,
};

const memoryCache = {
  eligibility: null as WithdrawalEligibility | null,
  balanceSummary: null as Record<string, BalanceSummary> | null,
  eligibilityTimestamp: 0,
  balanceTimestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000,
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Retry simple
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries = 2,
  baseDelay = 1000
): Promise<T> {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const delay = baseDelay * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
      return await requestFn();
    } catch (error: any) {
      lastError = error;

      // ne pas retry sur 4xx (sauf 429)
      const status = error?.response?.status;
      if (typeof status === "number" && status >= 400 && status < 500 && status !== 429) {
        break;
      }
    }
  }

  throw lastError;
}

// -------------------- Service --------------------

export const withdrawalService = {
  async checkEligibility(forceRefresh = false): Promise<WithdrawalEligibility> {
    const now = Date.now();

    if (
      !forceRefresh &&
      memoryCache.eligibility &&
      now - memoryCache.eligibilityTimestamp < memoryCache.CACHE_DURATION
    ) {
      return { ...memoryCache.eligibility, cached: true };
    }

    try {
      const resp = await retryRequest(async () => {
        return await axios.get<WithdrawalEligibilityResponse>(
          `${API_URL}/merchants/withdrawal/eligibility`,
          {
            timeout: API_TIMEOUTS.eligibility,
            headers: {
              ...getAuthHeaders(),
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );
      }, 2, 1000);

      // Adapter au shape attendu par le front (si besoin)
      const adapted: WithdrawalEligibility = {
        has_withdrawal_access: resp.data.has_withdrawal_access,
        merchant_code: "",
        username: "",
        allowed_channels: resp.data.allowed_channels,
        allowed_actions: resp.data.allowed_actions,
        allowed_operators: resp.data.allowed_operators,
      };

      memoryCache.eligibility = adapted;
      memoryCache.eligibilityTimestamp = now;

      return adapted;
    } catch (error: any) {
      if (memoryCache.eligibility) {
        return { ...memoryCache.eligibility, cached: true, error: error?.message };
      }
      return {
        has_withdrawal_access: false,
        merchant_code: "",
        username: "",
        cached: false,
        error: error?.message,
        allowed_channels: [],
        allowed_actions: [],
        allowed_operators: [],
      };
    }
  },

  async getBalanceSummary(): Promise<Record<string, BalanceSummary>> {
    const now = Date.now();

    if (memoryCache.balanceSummary && now - memoryCache.balanceTimestamp < memoryCache.CACHE_DURATION) {
      return memoryCache.balanceSummary;
    }

    try {
      const resp = await retryRequest(async () => {
        return await axios.get<Record<string, BalanceSummary>>(
          `${API_URL}/withdrawal/balance-summary`,
          {
            timeout: API_TIMEOUTS.balanceSummary,
            headers: getAuthHeaders(),
          }
        );
      });

      memoryCache.balanceSummary = resp.data;
      memoryCache.balanceTimestamp = now;

      return resp.data;
    } catch (error) {
      if (memoryCache.balanceSummary) return memoryCache.balanceSummary;
      return {};
    }
  },

  /**
   * ✅ Nouveau: initier cashout client depuis wallet withdrawal
   * Backend: POST /cashout/mobilemoney
   */
  async initiateWithdrawal(data: CustomerMobileMoneyCashoutRequest): Promise<TransferResponse> {
    try {
      const resp = await axios.post<TransferResponse>(
        `${API_URL}/merchants/internal-transfers/cashout/mobilemoney`,
        data,
        {
          timeout: API_TIMEOUTS.initiate,
          headers: getAuthHeaders(),
        }
      );

      // invalider cache balance après écriture
      memoryCache.balanceSummary = null;
      return resp.data;
    } catch (error: any) {
      let errorMessage = "Échec du cashout Mobile Money";

      const detail = error?.response?.data?.detail;
      if (detail) {
        errorMessage = typeof detail === "string" ? detail : JSON.stringify(detail);
      } else if (error?.code === "ECONNABORTED") {
        errorMessage = "Délai d'attente dépassé. Veuillez réessayer.";
      } else if (String(error?.message || "").includes("Network Error")) {
        errorMessage = "Erreur réseau. Vérifiez votre connexion.";
      }

      throw new Error(errorMessage);
    }
  },

  async getHistory(page: number = 1, limit: number = 10, status?: string): Promise<WithdrawalHistoryResponse> {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (status) params.append("status", status);

      const resp = await axios.get<WithdrawalHistoryResponse>(
        `${API_URL}/withdrawal/history?${params.toString()}`,
        {
          timeout: API_TIMEOUTS.history,
          headers: {
            ...getAuthHeaders(),
            "Cache-Control": `max-age=${60 * 5}`,
          },
        }
      );

      return resp.data;
    } catch (error) {
      return {
        transactions: [],
        pagination: { page: 1, limit: 10, total: 0, pages: 1 },
      };
    }
  },

  async initiateOperatorWithdrawal(data: WithdrawalInitiateRequest): Promise<WithdrawalInitiateResponse> {
    const resp = await axios.post<WithdrawalInitiateResponse>(
      `${API_URL}/withdrawal/initiate`,
      data,
      {
        timeout: 30000,
        headers: getAuthHeaders(),
      }
    );
    memoryCache.balanceSummary = null;
    return resp.data;
  },

  async resendOTP(reference: string): Promise<{ status: string; message?: string }> {
    const resp = await axios.post(
      `${API_URL}/withdrawal/resend-otp`,
      { reference },
      {
        timeout: API_TIMEOUTS.initiate,
        headers: getAuthHeaders(),
      }
    );
    return resp.data;
  },

  async getWithdrawalConfig(): Promise<any> {
    const resp = await axios.get(`${API_URL}/merchants/withdrawal-config`, {
      timeout: API_TIMEOUTS.eligibility,
      headers: getAuthHeaders(),
    });
    return resp.data;
  },

  async updateWithdrawalConfig(data: { email?: string; phone_airtel?: string; phone_mpesa?: string; phone_orange?: string; phone_afrimoney?: string }): Promise<any> {
    const resp = await axios.put(`${API_URL}/merchants/withdrawal-config`, data, {
      timeout: API_TIMEOUTS.initiate,
      headers: getAuthHeaders(),
    });
    return resp.data;
  },

  async cancelPendingWithdrawalConfig(): Promise<any> {
    const resp = await axios.delete(`${API_URL}/merchants/withdrawal-config/pending`, {
      timeout: API_TIMEOUTS.initiate,
      headers: getAuthHeaders(),
    });
    return resp.data;
  },

  clearCache(): void {
    memoryCache.eligibility = null;
    memoryCache.balanceSummary = null;
    memoryCache.eligibilityTimestamp = 0;
    memoryCache.balanceTimestamp = 0;
  },

  getCachedEligibility(): WithdrawalEligibility | null {
    const now = Date.now();
    if (memoryCache.eligibility && now - memoryCache.eligibilityTimestamp < memoryCache.CACHE_DURATION) {
      return { ...memoryCache.eligibility, cached: true };
    }
    return null;
  },

  getCachedBalance(): Record<string, BalanceSummary> | null {
    const now = Date.now();
    if (memoryCache.balanceSummary && now - memoryCache.balanceTimestamp < memoryCache.CACHE_DURATION) {
      return memoryCache.balanceSummary;
    }
    return null;
  },
};
